import { CountFrequency } from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import {
  BreakdownEntry,
  EmployeeCount,
  FormatType,
  fromBaseEntry,
  fromBreakdownEntry,
  EmployeeCountModalProps,
  TimeSeriesEntry,
  emptyEmployeeCountModalData,
  BaseEntry,
} from '../../../pages/FinancialForensicsPage/types/EmployeeCount.types';

const sum = (values: TimeSeriesEntry[]) =>
  values.reduce((acc, entry) => acc + (entry.value ?? 0), 0);

export const calculateAnnualAverage = (
  countFrequency: CountFrequency,
  values: TimeSeriesEntry[],
): number => {
  if (!values?.length) {
    return 0;
  }

  switch (countFrequency) {
    case 'Annually':
      return values[0].value ?? 0;
    case 'Quarterly':
      return Math.floor(sum(values) / 4);
    case 'Monthly':
      return Math.floor(sum(values) / 12);
    default:
      return 0;
  }
};

const getBreakdownLabels = (entry?: BreakdownEntry): string[] =>
  entry?.breakdown.map((item) => item.label) ?? [];

const getBreakdownAverages = (entry?: BreakdownEntry): number[] =>
  entry?.breakdown.map((item) =>
    calculateAnnualAverage(entry.typeOfCount, item.values),
  ) ?? [];

const toValidValues = (values: TimeSeriesEntry[] = []): TimeSeriesEntry[] =>
  values.filter((v) => v?.value !== null && v?.value !== undefined);

const buildBaseEntry = (
  data: EmployeeCountModalProps,
  values: TimeSeriesEntry[],
): BaseEntry => ({
  sourceOfData: data.source,
  typeOfCount: data.detailsType as CountFrequency,
  values: toValidValues(values),
  fileURLs: [],
});

const buildBreakdownEntry = (
  data: EmployeeCountModalProps,
): BreakdownEntry => ({
  sourceOfData: data.source,
  typeOfCount: data.detailsType as CountFrequency,
  breakdown: data.details.map((detail) => ({
    label: detail.type,
    values: toValidValues(detail.values),
  })),
  fileURLs: [],
});

export const formatColumnsforEmployeeCountTable = (
  data: EmployeeCount,
  formatType: FormatType,
): string[] => {
  switch (formatType) {
    case 'count':
      return ['Employees', 'Members (Employee + Dependent)'];
    case 'subscription':
      return getBreakdownLabels(data.employeeBySubscriptionType);
    case 'plan':
      return getBreakdownLabels(data.employeeByPlanType);
    default:
      return [];
  }
};

export const formatEmployeeCountTableData = (
  data: EmployeeCount,
  formatType: FormatType,
): number[] => {
  switch (formatType) {
    case 'count':
      return [
        calculateAnnualAverage(
          data.employees.typeOfCount,
          data.employees.values,
        ),
        calculateAnnualAverage(data.members.typeOfCount, data.members.values),
      ];
    case 'subscription':
      return getBreakdownAverages(data.employeeBySubscriptionType);
    case 'plan':
      return getBreakdownAverages(data.employeeByPlanType);
    default:
      return [];
  }
};

export const formatEmployeeCountModalData = (
  employeeCountType: FormatType,
  employeeCount: EmployeeCount,
): EmployeeCountModalProps => {
  switch (employeeCountType) {
    case 'count': {
      const entry = employeeCount.employees ?? employeeCount.members;
      return {
        id: '',
        source: entry?.sourceOfData ?? '',
        detailsType: entry?.typeOfCount,
        details: [
          fromBaseEntry('Employees', employeeCount.employees),
          fromBaseEntry(
            'Members (Employee + Dependent)',
            employeeCount.members,
          ),
        ],
      };
    }

    case 'subscription': {
      const entry = employeeCount.employeeBySubscriptionType;
      return {
        id: '',
        source: entry?.sourceOfData ?? '',
        detailsType: entry?.typeOfCount,
        details: fromBreakdownEntry(entry),
      };
    }

    case 'plan': {
      const entry = employeeCount.employeeByPlanType;
      return {
        id: '',
        source: entry?.sourceOfData ?? '',
        detailsType: entry?.typeOfCount,
        details: fromBreakdownEntry(entry),
      };
    }

    default:
      return emptyEmployeeCountModalData();
  }
};

export const formatUpdatedEmployeeCount = (
  formatType: FormatType,
  data: EmployeeCountModalProps,
) => {
  switch (formatType) {
    case 'count': {
      const employee = data.details.find((d) => d.type === 'Employees');
      const member = data.details.find(
        (d) => d.type === 'Members (Employee + Dependent)',
      );

      return {
        employees: buildBaseEntry(data, employee?.values ?? []),
        members: buildBaseEntry(data, member?.values ?? []),
      };
    }

    case 'subscription':
      return {
        employeeBySubscriptionType: buildBreakdownEntry(data),
      };

    case 'plan':
      return {
        employeeByPlanType: buildBreakdownEntry(data),
      };

    default:
      return {};
  }
};
